import { zodResolver } from "@hookform/resolvers/zod";
import {
	Button,
	Paper,
	PasswordInput,
	Stack,
	Text,
	TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router";

import {
	loginStaff,
	readStaffLoginError,
} from "@/features/auth/api/auth-staff";
import {
	defaultAuthenticatedPath,
	loginWithStaffSession,
} from "@/features/auth/lib/auth-store";
import {
	type LoginFormValues,
	loginSchema,
} from "@/features/auth/lib/login-schema";
import { useAuth } from "@/features/auth/lib/use-auth";
import { PlayBrand } from "@/shared/ui/play-brand";

export function LoginPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { isAuthenticated: alreadyIn } = useAuth();

	if (alreadyIn) {
		return <Navigate to={defaultAuthenticatedPath()} replace />;
	}

	return (
		<Stack
			align="center"
			justify="center"
			mih="100vh"
			px="md"
			style={{ background: "var(--play-canvas-bg)" }}
		>
			<Paper shadow="sm" p="xl" radius="lg" w="100%" maw={440} withBorder>
				<Stack gap="md">
					<Stack gap={8} align="center">
						<PlayBrand size="xl" markOnly />
						<Text c="dimmed" size="sm" ta="center">
							{t("login.subtitle")}
						</Text>
					</Stack>
					<StaffLoginForm
						onDone={() =>
							navigate(defaultAuthenticatedPath(), { replace: true })
						}
					/>
				</Stack>
			</Paper>
		</Stack>
	);
}

function StaffLoginForm({ onDone }: { onDone: () => void }) {
	const { t } = useTranslation();
	const {
		control,
		handleSubmit,
		formState: { isSubmitting },
	} = useForm<LoginFormValues>({
		resolver: zodResolver(loginSchema),
		defaultValues: { email: "", password: "" },
	});

	const onSubmit = handleSubmit(async (values) => {
		try {
			const session = await loginStaff(
				values.email.trim().toLowerCase(),
				values.password,
			);
			loginWithStaffSession(session);
			notifications.show({
				color: "green",
				title: t("login.signedIn"),
				message: t("login.signedInStaff", { role: session.account.role }),
			});
			onDone();
		} catch (error) {
			notifications.show({
				color: "red",
				title: t("login.staffFailed"),
				message: await readStaffLoginError(error),
			});
		}
	});

	return (
		<form onSubmit={onSubmit}>
			<Stack gap="sm">
				<Controller
					name="email"
					control={control}
					render={({ field, fieldState }) => (
						<TextInput
							{...field}
							label={t("login.email")}
							placeholder={t("login.emailPlaceholder")}
							error={fieldState.error?.message}
							autoComplete="username"
						/>
					)}
				/>
				<Controller
					name="password"
					control={control}
					render={({ field, fieldState }) => (
						<PasswordInput
							{...field}
							label={t("login.password")}
							placeholder={t("login.passwordPlaceholder")}
							error={fieldState.error?.message}
							autoComplete="current-password"
						/>
					)}
				/>
				<Button type="submit" loading={isSubmitting} fullWidth>
					{t("login.submit")}
				</Button>
			</Stack>
		</form>
	);
}
